import { ButtonHTMLAttributes, FC, Fragment, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../../../hooks/useDebounce";
import useI18n from "../../../../../../../hooks/useTranslations";
import { useApi } from "../../../../../../../http/api";
import { SearchAdminItem } from "../../../../../../../http/types/items";
import { useItemsApi } from "../../../../../../../http/useItems";
import {
  AdminWallMaterialFixing,
  AdminWallMaterialFixingItem,
} from "../../../../../../../http/vertigrip/types/wallMaterialFixings";
import { useWallMaterialFixingsApi } from "../../../../../../../http/vertigrip/useWallMaterialFixings";
import SvgAdd from "../../../../../../icons/Add";
import SvgRestore from "../../../../../../icons/Restore";
import { item } from "../../../../../../ui/BorderBox.module.css";
import Button from "../../../../../../ui/Button";
import ControlledNumericWithQuantity from "../../../../../../ui/ControlledNumericWithQuantity";
import FlexContent from "../../../../../../ui/FlexContent";
import Form from "../../../../../../ui/Form";
import FormField from "../../../../../../ui/FormField";
import FormFieldsContainer from "../../../../../../ui/FormFieldsContainer";
import Header from "../../../../../../ui/Header";
import useImageUploader from "../../../../../../ui/ImageUploader";
import Input from "../../../../../../ui/Input";
import ModalDialog from "../../../../../../ui/ModalDialog";
import Numeric from "../../../../../../ui/Numeric";
import { CustomAsyncSelect } from "../../../../../../ui/select/CustomSelect";
import Textarea from "../../../../../../ui/Textarea";
import TranslatableField from "../../../../../../ui/TranslatableField";
import VisibilityScope, { VisibilityScopeValue } from "../../../../../../ui/VisibilityScope";
import { VERTIGRIP } from "../../../../AdminSystemSettings";
import styles from "./Fixing.module.css";

export const NewFixing: FC = () => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { installationTypeId } = useParams<{ installationTypeId: string }>();
  const { wallMaterialId } = useParams<{ wallMaterialId: string }>();

  const path = [
    { url: `/admin/systems`, display: i18n.translation.systems.all },
    { url: `/admin/systems/${systemId}`, display: VERTIGRIP },
    {
      url: `/admin/systems/${systemId}/settings?tab=2`,
      display: `${i18n.translation.common.settings}: ${i18n.translation.installationTypes.plural}`,
    },
    {
      url: `/admin/systems/${systemId}/installation_types/${installationTypeId}?tab=5`,
      display: i18n.translation.wallMaterials.plural,
    },
    {
      url: `/admin/systems/${systemId}/installation_types/${installationTypeId}/wall_materials/${wallMaterialId}?tab=1`,
      display: i18n.translation.wallMaterialFixings.plural,
    },
    { display: i18n.translation.common.createNew },
  ];

  return (
    <>
      <Header title={`${i18n.translation.wallMaterialFixings.createNew}`} path={path} />
      <FlexContent padding direction="row">
        <Fixing wallMaterialId={wallMaterialId} mode="new" />
      </FlexContent>
    </>
  );
};

export const EditFixing: FC = () => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { installationTypeId } = useParams<{ installationTypeId: string }>();
  const { wallMaterialId } = useParams<{ wallMaterialId: string }>();
  const { fixingId } = useParams<{ fixingId: string }>();
  const wallMaterialFixingsApi = useWallMaterialFixingsApi();

  const aborter = useRef(new AbortController());
  const { error, data, isMutating, trigger } = wallMaterialFixingsApi.getAdminWallMaterialFixing(
    aborter.current.signal,
  );

  const path = [
    { url: `/admin/systems`, display: i18n.translation.systems.all },
    { url: `/admin/systems/${systemId}`, display: VERTIGRIP },
    {
      url: `/admin/systems/${systemId}/settings?tab=3`,
      display: `${i18n.translation.common.settings}: ${i18n.translation.installationTypes.plural}`,
    },
    {
      url: `/admin/systems/${systemId}/installation_types/${installationTypeId}?tab=5`,
      display: i18n.translation.wallMaterials.plural,
    },
    {
      url: `/admin/systems/${systemId}/installation_types/${installationTypeId}/wall_materials/${wallMaterialId}?tab=1`,
      display: i18n.translation.wallMaterialFixings.plural,
    },
    { display: data?.name! },
  ];

  useEffect(() => {
    fixingId && trigger(fixingId);
  }, [fixingId]);

  return (
    <>
      <Header
        title={`${i18n.translation.wallMaterialFixings.singular}: ${data?.name ?? ""}`}
        error={error}
        path={path}
      />
      <FlexContent padding direction="row">
        <Fixing
          fixing={!error ? data : undefined}
          wallMaterialId={undefined}
          disabled={isMutating || !!error}
          mode="edit"
        />
      </FlexContent>
    </>
  );
};

interface item {
  id: string;
  code: string;
  quantity: number;
  minConfigurableQuantity: number;
}

interface CreateWallMaterialFixingForm {
  name: string | null;
  description: string | null;
  descriptionIt: string | null;
  descriptionDe: string | null;
  descriptionEs: string | null;
  descriptionFr: string | null;
  order: number | null;
  items: item[];
  visibility: VisibilityScopeValue;
}

type FixingProps = {
  fixing?: AdminWallMaterialFixing;
  wallMaterialId?: string;
  disabled?: boolean;
  mode: "new" | "edit";
};

const Fixing: FC<FixingProps> = ({ fixing, wallMaterialId, disabled, mode }: FixingProps) => {
  const i18n = useI18n();
  const api = useApi();
  const { systemId } = useParams<{ systemId: string }>();
  const { installationTypeId } = useParams<{ installationTypeId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const wallMaterialFixingsApi = useWallMaterialFixingsApi();

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: fixing?.image,
    isLoading: mode === "edit" && !fixing,
    disabled,
  });

  const [originalItems, setOriginalItems] = useState<AdminWallMaterialFixingItem[]>(fixing?.items ?? []);

  const saveImage = async (id: string, file: File, callback: () => void | Promise<void>) => {
    try {
      const signedUrl = await wallMaterialFixingsApi.generateUploadImageUrl(id, file.name, file.type);
      const res = await api.uploadFile(signedUrl, file);
      if (!res.ok) {
        dispatch(showErrorNotification(i18n, res));
        setIsSubmitting(false);
        return;
      }
      onSave();
      await callback();
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultValues = {
    name: fixing?.name,
    description: fixing?.description,
    descriptionIt: fixing?.descriptionIt ?? null,
    descriptionDe: fixing?.descriptionDe ?? null,
    descriptionEs: fixing?.descriptionEs ?? null,
    descriptionFr: fixing?.descriptionFr ?? null,
    order: fixing?.order ?? 50,
    items: fixing?.items ?? [],
    visibility: fixing?.visibility,
  };

  return (
    <Form<CreateWallMaterialFixingForm>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      // disable only if it is a new fixing
      disabled={(isSubmitting && !fixing) || disabled}
      onSubmit={() => async (formData) => {
        if (formData.items.length === 0) {
          return;
        }

        setIsSubmitting(true);

        const itemsWithQuantities = formData.items.map((x) => ({ id: x.id, quantity: x.quantity }));

        const commonBody = {
          name: formData.name?.trim()!,
          order: formData.order!,
          description: formData.description!,
          descriptionIt: formData.descriptionIt?.trim() ?? null,
          descriptionDe: formData.descriptionDe?.trim() ?? null,
          descriptionEs: formData.descriptionEs?.trim() ?? null,
          descriptionFr: formData.descriptionFr?.trim() ?? null,
          wallMaterialId: fixing?.wallMaterial.id ?? wallMaterialId!,
          image: newImage?.name,
          items: itemsWithQuantities,
          visibility: formData.visibility,
        };

        if (fixing?.id) {
          const onSuccess = async () => {
            try {
              await wallMaterialFixingsApi.updateAdminWallMaterialFixing(fixing.id, {
                ...commonBody,
                removeImage: removeImage,
              });
              dispatch(showSuccessNotification(i18n.translation));
            } catch (error) {
              dispatch(showErrorNotification(i18n, error));
            } finally {
              setIsSubmitting(false);
            }
          };
          newImage?.name ? await saveImage(fixing.id, newImage, onSuccess) : await onSuccess();
        } else {
          try {
            const res = await wallMaterialFixingsApi.insertAdminWallMaterialFixing({
              ...commonBody,
            });
            const onSuccess = () => {
              setIsSubmitting(false);
              navigate(
                `/admin/systems/${systemId}/installation_types/${installationTypeId}/wall_materials/${wallMaterialId}/fixings/${res.id}`,
                { replace: true },
              );
            };
            newImage?.name ? await saveImage(res.id, newImage, onSuccess) : onSuccess();
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
          } finally {
            setIsSubmitting(false);
          }
        }
      }}
    >
      {({ control, setValue, register, formState: { errors }, reset, setError, clearErrors, watch }) => {
        useEffect(() => {
          fixing?.id && reset(defaultValues);
          setValue("items", fixing?.items ?? []);
          setOriginalItems(fixing?.items ?? []);
        }, [fixing?.id]);

        useEffect(() => {
          register("descriptionIt");
          register("descriptionDe");
          register("descriptionEs");
          register("descriptionFr");
        }, [register]);

        const items = watch("items");
        const values = watch();

        useEffect(() => {
          items?.some((i) => !i.quantity) ? setError("items", { type: "min" }) : clearErrors("items");
        }, [items]);

        return (
          <FormFieldsContainer imageUploader={component}>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isInvalid, isOptional }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={disabled}
                  {...register("name", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.description} error={errors.description}>
              {({ labelId, isOptional, isInvalid }) => {
                return (
                  <TranslatableField
                    field="description"
                    formValues={values}
                    setFormValue={setValue}
                    dialogTitle={i18n.translation.common.description}
                    dialogType="textarea"
                  >
                    <Textarea
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={disabled}
                      {...register("description", {
                        required: !isOptional,
                        minLength: 1,
                      })}
                    />
                  </TranslatableField>
                );
              }}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={disabled}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="items"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: i18n.translation.common.atLeastOneItemNeeded,
                },
                validate: (items) => {
                  if (items.some((i) => !i.quantity)) {
                    return i18n.translation.common.fieldContainsInvalidValueInsertBiggerOne;
                  }
                  return undefined;
                },
              }}
              disabled={disabled}
              defaultValue={defaultValues.items}
              render={({ field, fieldState }) => (
                <>
                  <FormField
                    label={`${i18n.translation.items.plural} (+${i18n.translation.common.qty})`}
                    error={fieldState.error}
                  >
                    <ControlledNumericWithQuantity.List>
                      {field.value.map((x) => (
                        <Fragment key={x.id}>
                          <ControlledNumericWithQuantity
                            item={{
                              id: x.id,
                              display: x.code,
                              disabled: true,
                            }}
                            controls={{
                              scale: x.minConfigurableQuantity ?? 1,
                            }}
                            quantity={{
                              quantity: x.quantity,
                              decimalScale: x.minConfigurableQuantity?.toString().split(".")[1]?.length ?? 0,
                              isInvalid: x.quantity <= 0,
                              onUpdate: (id, quantity) => {
                                setValue(
                                  "items",
                                  field.value.reduce((rv: item[], i) => {
                                    if (i.id === id) {
                                      return [...rv, { ...i, quantity }];
                                    }
                                    return [...rv, i];
                                  }, []),
                                );
                              },
                            }}
                            onDelete={(id) =>
                              setValue(
                                "items",
                                field.value.reduce((rv: item[], i) => {
                                  if (i.id === id) {
                                    return rv;
                                  }
                                  return [...rv, i];
                                }, []),
                              )
                            }
                          />
                        </Fragment>
                      ))}
                    </ControlledNumericWithQuantity.List>
                  </FormField>
                  <div className={styles.itemsButtonContainer}>
                    <Button
                      glyph={SvgRestore}
                      type="secondary"
                      buttonProps={{
                        disabled,
                        onClick: () => {
                          setValue("items", originalItems);
                          if (originalItems.length > 0) {
                            clearErrors("items");
                          }
                        },
                        title: i18n.translation.common.reset,
                      }}
                    />
                    <AddItemButton
                      disabled={disabled}
                      onAdd={(item) => {
                        if (field.value.find((x) => x.id === item.id)) {
                          dispatch(
                            showErrorNotification(
                              i18n,
                              null,
                              i18n.translation.wallMaterialFixingItems.errors.alreadyPresent,
                            ),
                          );
                          return false;
                        }
                        setValue("items", [
                          ...field.value,
                          {
                            id: item.id,
                            code: item.code,
                            quantity: item.minConfigurableQuantity ?? 1,
                            minConfigurableQuantity: item.minConfigurableQuantity ?? 1,
                          },
                        ]);
                        return true;
                      }}
                    />
                  </div>
                </>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

type AddItemButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> & {
  onAdd: (item: SearchAdminItem) => boolean;
};

const AddItemButton: FC<AddItemButtonProps> = ({ onAdd, ...rest }: AddItemButtonProps) => {
  const i18n = useI18n();
  const [showNewItemDialog, setShowNewItemDialog] = useState(false);
  const searchTerm = useRef("");
  const aborter = useRef(new AbortController());
  const itemsApi = useItemsApi();
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

  // Fetch initial data on mount
  useEffect(() => {
    loadOptions(searchTerm.current).then(setDefaultOptions);
  }, []);

  const [item, setItem] = useState<SearchAdminItem | null>(null);

  return (
    <>
      <Button glyph={SvgAdd} type="secondary" buttonProps={{ ...rest, onClick: () => setShowNewItemDialog(true) }} />
      {showNewItemDialog && (
        <ModalDialog
          onClose={() => setShowNewItemDialog(false)}
          title={i18n.translation.common.addNewItem}
          isSubmitting={false}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setShowNewItemDialog(false),
              type: "secondary",
            },
            {
              onClick: () => {
                if (item) {
                  const success = onAdd(item);

                  if (success) {
                    setShowNewItemDialog(false);
                    setItem(null);
                  }
                }
              },
              title: i18n.translation.common.add,
            },
          ]}
        >
          <CustomAsyncSelect
            defaultOptions={defaultOptions}
            loadOptions={loadOptions}
            isClearable
            getOptionLabel={(x) => x.code}
            getOptionValue={(x) => x.id}
            value={item}
            onChange={(value) => {
              searchTerm.current = "";
              setItem(value);
            }}
          />
        </ModalDialog>
      )}
    </>
  );
};
